import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type { ReactElement } from 'react';
import { Layout } from './components/Layout';
import { Spinner } from './components/ui';
import { useAuth } from './hooks/useAuth';
import { Dashboard } from './pages/Dashboard';
import { Destinations } from './pages/Destinations';
import { History } from './pages/History';
import { Login } from './pages/Login';
import { OrderDetail } from './pages/OrderDetail';
import { Settings } from './pages/Settings';
import { Trade } from './pages/Trade';
import { SiteLayout } from './site/SiteLayout';
import { Home } from './site/Home';
import { Services } from './site/Services';
import { Approach } from './site/Approach';
import { Contact } from './site/Contact';

/**
 * The exchange app is mounted under /app only when this is explicitly on.
 *
 * It stays in the bundle and in the repo either way — the INR/USDT
 * orchestration, its ledger and its state machine are finished work waiting
 * on a payment provider, not abandoned code. No provider is wired in this
 * tree, so the public site is all that should be reachable: a trading UI
 * that cannot complete a trade is worse than no trading UI at all.
 *
 * Set VITE_ENABLE_EXCHANGE=true at build time to mount it. It is off by
 * default so that forgetting the flag fails closed.
 */
const EXCHANGE_ENABLED = import.meta.env.VITE_ENABLE_EXCHANGE === 'true';

function RequireAuth({ children }: { children: ReactElement }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="center-page" style={{ textAlign: 'center' }}>
        <Spinner />
      </div>
    );
  }
  if (!user) return <Navigate to="/app/login" state={{ from: location.pathname }} replace />;
  return children;
}

export function App() {
  return (
    <Routes>
      {/* Public site */}
      <Route element={<SiteLayout />}>
        <Route index element={<Home />} />
        <Route path="services" element={<Services />} />
        <Route path="approach" element={<Approach />} />
        <Route path="contact" element={<Contact />} />
      </Route>

      {/* Signed-in exchange app, gated at build time. */}
      {EXCHANGE_ENABLED && (
        <>
          <Route path="/app/login" element={<Login />} />
          <Route
            path="/app"
            element={
              <RequireAuth>
                <Layout />
              </RequireAuth>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="trade" element={<Trade />} />
            <Route path="destinations" element={<Destinations />} />
            <Route path="history" element={<History />} />
            <Route path="orders/:id" element={<OrderDetail />} />
            <Route path="settings" element={<Settings />} />
          </Route>
        </>
      )}

      {/* Unknown paths land on the public home page rather than a blank screen. */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
